// useGraphClipboard — the one React binding for graph copy/cut/paste.
//
// Both graph editors mount this: BlueprintEditor (body + face) and EventGraph
// (its main canvas, its graphGroup editor and its function editor). It owns the
// document-level key listeners, the transient status message, and the pointer
// position a paste lands at — so neither editor carries its own copy of any of
// that, and the two cannot drift apart in what a chord does.
//
// The rules live in nodeClipboard.js; the gestures live in
// graphClipboardActions.js. This is only the wiring, and it is deliberately
// thin: no clipboard logic is duplicated here.
//
// FEEDBACK IS NOT OPTIONAL. Every gesture reports — copied N, pasted N, refused
// because, blocked because. A clipboard that silently does nothing is
// indistinguishable from a broken one, which is where pressing Ctrl+V five
// times comes from. The message renders in the canvas next to the nodes it
// describes (the proximity rule), not in a global status bar.

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  setClipboardStatus,
  statusForSurface,
  subscribeClipboardStatus,
} from './graphClipboardStatusStore.js'
import {
  isCopyKeyEvent,
  isCutKeyEvent,
  isPasteKeyEvent,
  copySelection,
  cutSelection,
  pasteIntoGraph,
} from './graphClipboardActions.js'

/**
 * Window in which the keydown and native-paste notifications of ONE Ctrl+V are
 * treated as the same gesture. Comfortably longer than the gap a browser puts
 * between them, far shorter than any deliberate second paste.
 */
export const PASTE_GESTURE_MS = 250

/**
 * @param {object} host  see graphClipboardActions' host contract. Read through
 *   a ref, so a changing identity is harmless — but the listeners subscribe
 *   ONCE, because re-subscribing on every node edit is churn on the edit path.
 * @param {object} opts
 *   surface  — 'body' | 'face' | 'events' | a container id. Scopes the status
 *              message, so only the canvas that reported shows a pill.
 *   enabled  — false disables every chord (read-only collab, or a canvas whose
 *              drill-in view owns its own state).
 *   flowRef  — { current: { screenToFlowPosition } } from ReactFlow, so a paste
 *              lands under the pointer rather than at a fixed nudge.
 */
export function useGraphClipboard(host, { surface, enabled = true, flowRef = null } = {}) {
  // The message lives in a module singleton, not here — see
  // graphClipboardStatusStore. Several graph canvases are mounted at once (the
  // hidden ones stay in the DOM), so it is scoped by `surface` and only the
  // canvas that reported renders a pill.
  const [status, setStatus] = useState(() => statusForSurface(surface))

  const hostRef = useRef(host); hostRef.current = host
  const enabledRef = useRef(enabled); enabledRef.current = enabled
  const flow = useRef(flowRef); flow.current = flowRef
  const surfaceRef = useRef(surface); surfaceRef.current = surface

  // Last pointer position over the canvas, in FLOW coordinates. Unreal pastes
  // where you are looking, not where the nodes came from.
  const pointer = useRef(null)

  useEffect(() => subscribeClipboardStatus(() => {
    setStatus(statusForSurface(surfaceRef.current))
  }), [])

  const report = useCallback((r) => {
    setClipboardStatus(surfaceRef.current, r ?? {})
  }, [])

  /** Attach to the ReactFlow pane's onPointerMove. */
  const onPointerMove = useCallback((e) => {
    const toFlow = flow.current?.current?.screenToFlowPosition
    if (typeof toFlow !== 'function') return
    try {
      pointer.current = toFlow({ x: e.clientX, y: e.clientY })
    } catch {
      // A pane not yet measured throws; a paste then falls back to the nudge,
      // which is a placement difference, not a failure.
      pointer.current = null
    }
  }, [])

  const withReport = useCallback(() => ({ ...hostRef.current, report }), [report])

  const doCopy = useCallback(() => copySelection(withReport()), [withReport])
  const doCut = useCallback(() => cutSelection(withReport()), [withReport])

  // ── One gesture, two notifications ────────────────────────────────────────
  //
  // A Ctrl+V reaches us twice: through the keydown listener, and through the
  // pane's native `paste` event (which we deliberately do NOT preventDefault,
  // because its clipboardData needs no permission and is the only path that
  // works where readText() is restricted). Both are the SAME user gesture, so
  // whichever arrives first claims it. Without this the payload lands twice in
  // some browsers and once in others — broken in the way that is hardest to
  // diagnose, because it depends on the browser.
  const lastPaste = useRef(0)
  const claimPasteGesture = () => {
    const now = Date.now()
    if (now - lastPaste.current < PASTE_GESTURE_MS) return false
    lastPaste.current = now
    return true
  }

  const doPaste = useCallback((opts = {}) => {
    if (!claimPasteGesture()) return undefined
    return pasteIntoGraph(withReport(), { at: opts.at ?? pointer.current, ...opts })
  }, [withReport])

  useEffect(() => {
    const onKey = (e) => {
      if (!enabledRef.current) return
      // preventDefault only once the chord is known to be ours AND fired
      // outside an editable field (the predicates enforce the second half), so
      // native text copy/paste inside a node's fields is never intercepted.
      if (isCopyKeyEvent(e)) { e.preventDefault(); doCopy(); return }
      if (isCutKeyEvent(e)) { e.preventDefault(); doCut(); return }
      if (isPasteKeyEvent(e)) doPaste()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [doCopy, doCut, doPaste])

  /**
   * Attach to the pane's onPaste — the permission-free path, preferred wherever
   * a real paste event is available (Firefox restricts readText()).
   */
  const onPaste = useCallback((e) => {
    if (!enabledRef.current) return
    const text = e.clipboardData?.getData('text/plain')
    if (!text) return
    doPaste({ text })
  }, [doPaste])

  return { status, report, doCopy, doCut, doPaste, onPointerMove, onPaste }
}
